import { useEffect, useRef, type ReactNode } from "react";
import { Navigate, useParams } from "react-router";
import { Button } from "@/components/Button";
import { EmptyState } from "@/components/EmptyState";
import { FullPageStatus } from "@/components/FullPageStatus";
import { hasRole } from "@/lib/hasRole";
import { useAuth } from "./AuthContext";

export function ProtectedRoute({
  children,
  requiredRole,
}: {
  children: ReactNode;
  requiredRole?: "Admin" | "Owner";
}) {
  const {
    accessToken,
    user,
    isLoading,
    renewFailed,
    rateLimited,
    signedOut,
    login,
  } = useAuth();
  const { orgId } = useParams();
  const hasRequestedLogin = useRef(false);

  const needsLogin = !isLoading && !accessToken;
  const mustSignInDeliberately = renewFailed || signedOut;

  useEffect(() => {
    // Signing in is a full-page redirect, and login() clears the silent-renew
    // flag. Retrying automatically after a failed renew sends the browser round
    // that loop for as long as the token endpoint keeps refusing.
    if (!needsLogin || mustSignInDeliberately || hasRequestedLogin.current) {
      return;
    }

    hasRequestedLogin.current = true;

    void login();
  }, [needsLogin, mustSignInDeliberately, login]);

  if (isLoading) {
    return <FullPageStatus title="Loading..." />;
  }

  if (!accessToken) {
    if (!mustSignInDeliberately) {
      return null;
    }

    return (
      <FullPageStatus
        title={
          rateLimited
            ? "Too many sign in attempts"
            : signedOut
              ? "You're signed out"
              : "Your session has expired"
        }
        description={
          rateLimited
            ? "Wait about a minute, then sign in again."
            : signedOut
              ? "Sign in to get back to your organization."
              : "Sign in again to get back to your organization."
        }
      >
        <Button variant="primary" onClick={() => void login()}>
          Sign in
        </Button>
      </FullPageStatus>
    );
  }

  if (orgId && user?.orgId && orgId !== user.orgId) {
    return <Navigate to={`/${user.orgId}`} replace />;
  }

  if (requiredRole && !hasRole(user?.roles, requiredRole)) {
    return (
      <EmptyState
        title="You don't have access to this page"
        description="Ask an administrator in your organization if you need it."
      />
    );
  }

  return <>{children}</>;
}
